import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    trn: '',
    address: '',
    city: '',
    emirate: '',
    country: 'UAE',
    contactPerson: '',
    bankName: '',
    iban: '',
    swift: '',
    paymentTerms: 'Net 30',
    currency: 'AED',
    category: 'General',
    rating: 5,
    minOrderValue: 0,
    leadTime: 7,
    status: 'Active'
  });

  const [errors, setErrors] = useState({});

  // UAE Emirates list
  const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

  // Sample supplier data
  const sampleSuppliers = [
    {
      id: 1,
      name: 'Emirates Industrial Supply Co.',
      email: 'sales@emiratesindust.ae',
      phone: '+971-4-2345678',
      trn: '100523456789001',
      address: 'Building A, Industrial Complex, Jebel Ali, Dubai',
      city: 'Dubai',
      emirate: 'Dubai',
      country: 'UAE',
      contactPerson: 'Ali Al Mazrouei',
      bankName: 'First Abu Dhabi Bank',
      iban: 'AE07 0331 1234 5678 9012 345',
      swift: 'FABAAEAD',
      paymentTerms: 'Net 30',
      currency: 'AED',
      category: 'Equipment',
      rating: 4.5,
      minOrderValue: 5000,
      leadTime: 7,
      status: 'Active',
      createdAt: '2026-01-20',
      totalOrders: 15,
      totalSpent: 350000
    },
    {
      id: 2,
      name: 'Gulf Trading & Distribution LLC',
      email: 'procurement@gulftrading.ae',
      phone: '+971-2-4567890',
      trn: '100634567890002',
      address: 'Warehouse 12, Port Road, Abu Dhabi',
      city: 'Abu Dhabi',
      emirate: 'Abu Dhabi',
      country: 'UAE',
      contactPerson: 'Fatima Al Falahi',
      bankName: 'Abu Dhabi Islamic Bank',
      iban: 'AE21 0331 5678 9012 3456 789',
      swift: 'ADIBAEA',
      paymentTerms: 'Net 45',
      currency: 'AED',
      category: 'Raw Materials',
      rating: 4.8,
      minOrderValue: 10000,
      leadTime: 14,
      status: 'Active',
      createdAt: '2026-02-10',
      totalOrders: 22,
      totalSpent: 750000
    },
    {
      id: 3,
      name: 'Sharjah Logistics & Services',
      email: 'orders@sharjahlogistics.ae',
      phone: '+971-6-3456789',
      trn: '100745678901003',
      address: 'Unit 45, Sharjah Airport Free Zone',
      city: 'Sharjah',
      emirate: 'Sharjah',
      country: 'UAE',
      contactPerson: 'Hassan Al Zaabi',
      bankName: 'Emirates NBD',
      iban: 'AE07 0331 9012 3456 7890 123',
      swift: 'EBILAEA',
      paymentTerms: 'Net 15',
      currency: 'AED',
      category: 'Logistics',
      rating: 4.2,
      minOrderValue: 2000,
      leadTime: 5,
      status: 'Active',
      createdAt: '2026-03-01',
      totalOrders: 8,
      totalSpent: 125000
    },
    {
      id: 4,
      name: 'Premium Office Solutions',
      email: 'corporate@premiumoffice.ae',
      phone: '+971-50-5678901',
      trn: '100856789012004',
      address: 'Office Suite 301, Business Center, Dubai',
      city: 'Dubai',
      emirate: 'Dubai',
      country: 'UAE',
      contactPerson: 'Mohammed Al Mansoori',
      bankName: 'Dubai Islamic Bank',
      iban: 'AE76 0677 0123 4567 8901 234',
      swift: 'DIBDAEAD',
      paymentTerms: 'Net 60',
      currency: 'AED',
      category: 'Office Supplies',
      rating: 4.6,
      minOrderValue: 500,
      leadTime: 2,
      status: 'Active',
      createdAt: '2026-03-05',
      totalOrders: 32,
      totalSpent: 280000
    },
    {
      id: 5,
      name: 'International Tech Imports',
      email: 'imports@intechimports.com',
      phone: '+971-4-6789012',
      trn: '100967890123005',
      address: 'Free Zone Building 7, Jebel Ali, Dubai',
      city: 'Dubai',
      emirate: 'Dubai',
      country: 'UAE',
      contactPerson: 'David Chen',
      bankName: 'Standard Chartered Bank',
      iban: 'AE77 0661 2345 6789 0123 456',
      swift: 'SCBLAEADIOC',
      paymentTerms: 'Net 30',
      currency: 'USD',
      category: 'Technology',
      rating: 4.4,
      minOrderValue: 15000,
      leadTime: 21,
      status: 'Active',
      createdAt: '2026-03-08',
      totalOrders: 5,
      totalSpent: 450000
    }
  ];

  // Initialize with sample data
  useEffect(() => {
    setSuppliers(sampleSuppliers);
  }, []);

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.phone.includes(searchTerm) ||
    supplier.trn.includes(searchTerm) ||
    supplier.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Supplier name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';

    if (!formData.trn.trim()) {
      newErrors.trn = 'TRN is required for all suppliers';
    }

    if (formData.trn && formData.trn.length !== 15) {
      newErrors.trn = 'TRN must be 15 digits';
    }

    if (!formData.bankName.trim()) newErrors.bankName = 'Bank name is required';
    if (!formData.iban.trim()) newErrors.iban = 'IBAN is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (editingSupplier) {
      // Update existing supplier
      setSuppliers(prev => prev.map(supplier =>
        supplier.id === editingSupplier.id
          ? { ...supplier, ...formData, updatedAt: new Date().toISOString().split('T')[0] }
          : supplier
      ));
      setEditingSupplier(null);
    } else {
      // Add new supplier
      const newSupplier = {
        ...formData,
        id: Math.max(...suppliers.map(s => s.id), 0) + 1,
        createdAt: new Date().toISOString().split('T')[0],
        totalOrders: 0,
        totalSpent: 0
      };
      setSuppliers(prev => [...prev, newSupplier]);
    }

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      trn: '',
      address: '',
      city: '',
      emirate: '',
      country: 'UAE',
      contactPerson: '',
      bankName: '',
      iban: '',
      swift: '',
      paymentTerms: 'Net 30',
      currency: 'AED',
      category: 'General',
      rating: 5,
      minOrderValue: 0,
      leadTime: 7,
      status: 'Active'
    });

    setShowAddForm(false);
  };

  // Handle edit supplier
  const handleEdit = (supplier) => {
    setFormData(supplier);
    setEditingSupplier(supplier);
    setShowAddForm(true);
  };

  // Handle delete supplier
  const handleDelete = (supplierId) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      setSuppliers(prev => prev.filter(supplier => supplier.id !== supplierId));
    }
  };

  // Cancel form
  const handleCancel = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      trn: '',
      address: '',
      city: '',
      emirate: '',
      country: 'UAE',
      contactPerson: '',
      bankName: '',
      iban: '',
      swift: '',
      paymentTerms: 'Net 30',
      currency: 'AED',
      category: 'General',
      rating: 5,
      minOrderValue: 0,
      leadTime: 7,
      status: 'Active'
    });
    setEditingSupplier(null);
    setShowAddForm(false);
    setErrors({});
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  // Render star rating
  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary via-secondary to-accent rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Supplier Management</h1>
              <p className="text-white/80">Manage your supplier database and procurement relationships</p>
              <div className="mt-4 flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🇦🇪</span>
                  <span>UAE Compliant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">📦</span>
                  <span>{suppliers.length} Suppliers</span>
                </div>
              </div>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-4">
              <span className="text-2xl font-bold">🏭</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-8">
          {/* Action Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="form-control">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    className="input input-bordered"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-square">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg hover:scale-105 transition-transform shadow-lg"
              onClick={() => setShowAddForm(true)}
            >
              ➕ Add Supplier
            </button>
          </div>

          {/* Add/Edit Supplier Form */}
          {showAddForm && (
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl mb-6">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Supplier Name *</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className={`input input-bordered border-2 ${errors.name ? 'input-error' : ''}`}
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter supplier name"
                    />
                    {errors.name && <span className="text-error text-sm mt-1">{errors.name}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Email *</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className={`input input-bordered border-2 ${errors.email ? 'input-error' : ''}`}
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="supplier@example.com"
                    />
                    {errors.email && <span className="text-error text-sm mt-1">{errors.email}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Phone *</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className={`input input-bordered border-2 ${errors.phone ? 'input-error' : ''}`}
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+971-4-1234567"
                    />
                    {errors.phone && <span className="text-error text-sm mt-1">{errors.phone}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">TRN (Tax Registration) *</span>
                    </label>
                    <input
                      type="text"
                      name="trn"
                      className={`input input-bordered border-2 font-mono ${errors.trn ? 'input-error' : ''}`}
                      value={formData.trn}
                      onChange={handleInputChange}
                      placeholder="15-digit TRN"
                      maxLength="15"
                    />
                    {errors.trn && <span className="text-error text-sm mt-1">{errors.trn}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Category</span>
                    </label>
                    <select
                      name="category"
                      className="select select-bordered border-2"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      <option value="General">General</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Raw Materials">Raw Materials</option>
                      <option value="Office Supplies">Office Supplies</option>
                      <option value="Technology">Technology</option>
                      <option value="Logistics">Logistics</option>
                      <option value="Services">Services</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Contact Person</span>
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      className="input input-bordered border-2"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      placeholder="Primary contact person"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Address</span>
                    </label>
                    <textarea
                      name="address"
                      className="textarea textarea-bordered border-2 h-20"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street address, building, warehouse, etc."
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">City</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        className="input input-bordered border-2"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-semibold">Emirate</span>
                      </label>
                      <select
                        name="emirate"
                        className="select select-bordered border-2"
                        value={formData.emirate}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Emirate</option>
                        {emirates.map(emirate => (
                          <option key={emirate} value={emirate}>{emirate}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Bank & Payment Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Bank Name *</span>
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      className={`input input-bordered border-2 ${errors.bankName ? 'input-error' : ''}`}
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="Emirates NBD, FAB, etc."
                    />
                    {errors.bankName && <span className="text-error text-sm mt-1">{errors.bankName}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">IBAN *</span>
                    </label>
                    <input
                      type="text"
                      name="iban"
                      className={`input input-bordered border-2 font-mono ${errors.iban ? 'input-error' : ''}`}
                      value={formData.iban}
                      onChange={handleInputChange}
                      placeholder="AE07 0331 1234 5678 9012 345"
                    />
                    {errors.iban && <span className="text-error text-sm mt-1">{errors.iban}</span>}
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">SWIFT Code</span>
                    </label>
                    <input
                      type="text"
                      name="swift"
                      className="input input-bordered border-2 font-mono"
                      value={formData.swift}
                      onChange={handleInputChange}
                      placeholder="EBILAEA"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Currency</span>
                    </label>
                    <select
                      name="currency"
                      className="select select-bordered border-2"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </div>

                {/* Procurement Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Payment Terms</span>
                    </label>
                    <select
                      name="paymentTerms"
                      className="select select-bordered border-2"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Min. Order Value ({formData.currency})</span>
                    </label>
                    <input
                      type="number"
                      name="minOrderValue"
                      className="input input-bordered border-2"
                      value={formData.minOrderValue}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                      placeholder="0"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Lead Time (Days)</span>
                    </label>
                    <input
                      type="number"
                      name="leadTime"
                      className="input input-bordered border-2"
                      value={formData.leadTime}
                      onChange={handleInputChange}
                      min="1"
                      step="1"
                      placeholder="7"
                    />
                  </div>
                </div>

                {/* Rating & Status */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Rating</span>
                    </label>
                    <select
                      name="rating"
                      className="select select-bordered border-2"
                      value={formData.rating}
                      onChange={handleInputChange}
                    >
                      <option value="5">⭐⭐⭐⭐⭐ 5.0</option>
                      <option value="4.5">⭐⭐⭐⭐✨ 4.5</option>
                      <option value="4">⭐⭐⭐⭐☆ 4.0</option>
                      <option value="3.5">⭐⭐⭐✨☆ 3.5</option>
                      <option value="3">⭐⭐⭐☆☆ 3.0</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Status</span>
                    </label>
                    <select
                      name="status"
                      className="select select-bordered border-2"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Country</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      className="input input-bordered border-2"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="UAE"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="btn btn-ghost btn-lg hover:bg-base-200"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg"
                  >
                    {editingSupplier ? '💾 Update Supplier' : '💾 Save Supplier'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Suppliers Table */}
          <div className="bg-base-100 rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th className="rounded-tl-lg">Supplier</th>
                    <th>Contact</th>
                    <th>TRN</th>
                    <th>Category</th>
                    <th>Rating</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th className="rounded-tr-lg">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-base-200 transition-colors">
                      <td>
                        <div>
                          <div className="font-bold text-primary">{supplier.name}</div>
                          <div className="text-sm text-gray-500">{supplier.contactPerson}</div>
                          <div className="text-xs text-gray-400">{supplier.createdAt}</div>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div>{supplier.email}</div>
                          <div className="text-gray-500">{supplier.phone}</div>
                        </div>
                      </td>
                      <td>
                        <span className="font-mono text-sm">
                          {supplier.trn}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-outline">
                          {supplier.category}
                        </span>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className={star <= supplier.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">{supplier.rating.toFixed(1)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <p className="font-semibold">{supplier.paymentTerms}</p>
                          <p className="text-gray-500">Lead: {supplier.leadTime} days</p>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          supplier.status === 'Active'
                            ? 'badge-success'
                            : supplier.status === 'Inactive'
                            ? 'badge-warning'
                            : 'badge-error'
                        }`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-xs btn-outline btn-info"
                            onClick={() => handleEdit(supplier)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-xs btn-outline btn-error"
                            onClick={() => handleDelete(supplier.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredSuppliers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏭</div>
                <h3 className="text-xl font-bold text-gray-600 mb-2">No suppliers found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first supplier'}
                </p>
                {!searchTerm && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddForm(true)}
                  >
                    Add Your First Supplier
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="stat bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl">
              <div className="stat-figure text-primary">
                <span className="text-2xl">🏭</span>
              </div>
              <div className="stat-title">Total Suppliers</div>
              <div className="stat-value text-primary text-2xl">{suppliers.length}</div>
            </div>

            <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
              <div className="stat-figure text-success">
                <span className="text-2xl">📦</span>
              </div>
              <div className="stat-title">Total Orders</div>
              <div className="stat-value text-success text-2xl">
                {suppliers.reduce((sum, s) => sum + s.totalOrders, 0)}
              </div>
            </div>

            <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
              <div className="stat-figure text-warning">
                <span className="text-2xl">💰</span>
              </div>
              <div className="stat-title">Total Spent</div>
              <div className="stat-value text-warning text-2xl">
                {formatCurrency(suppliers.reduce((sum, s) => sum + s.totalSpent, 0))}
              </div>
            </div>

            <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
              <div className="stat-figure text-info">
                <span className="text-2xl">⭐</span>
              </div>
              <div className="stat-title">Avg. Rating</div>
              <div className="stat-value text-info text-2xl">
                {(suppliers.reduce((sum, s) => sum + s.rating, 0) / suppliers.length || 0).toFixed(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-base-500 mt-4">
          <p>🇦🇪 UAE Federal Tax Authority Compliant | Supplier Management System</p>
        </div>
      </div>
    </div>
  );
};

export default Suppliers;